import React, { useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { ChatBubbleBottomCenterTextIcon } from '@heroicons/react/24/outline';
import { useAltHotkeys } from '../../hooks/useAltHotkeys';
import { useModalDrag } from '../../hooks/useModalDrag';
import ModalHeader from '../ui/ModalHeader';

export interface DriverCommentModalProps {
  unitnumber: string;
  driverName: string;
  /** Today's driverstatus note as the lane currently shows it (null = none yet) */
  initialComment: string | null;
  /** Disables the controls and shows a saving label while the write is in flight */
  saving?: boolean;
  /** Receives the trimmed note; an empty string clears it */
  onSave: (comment: string) => void;
  onClose: () => void;
}

/**
 * Add / edit / clear a driver's status note from the Driver Status Monitor lane
 * header. Edits today's `driverstatus.comments` -- the same text the header's
 * comment icon shows on hover and the Driver Board edits in its status modal.
 *
 * Rendered only while open (the parent unmounts it), so unmount resets state.
 */
export function DriverCommentModal({
  unitnumber,
  driverName,
  initialComment,
  saving = false,
  onSave,
  onClose,
}: DriverCommentModalProps) {
  const original = initialComment ?? '';
  const [comment, setComment] = useState(original);

  const trimmed = comment.trim();
  const isDirty = trimmed !== original.trim();
  const canSave = isDirty && !saving;

  const handleSave = () => {
    if (!canSave) return;
    onSave(trimmed);
  };

  // Alt+S saves, Alt+C cancels -- the same chords as every other modal on this
  // board. `layer: true` keeps them from reaching the page underneath.
  const rootRef = useRef<HTMLDivElement | null>(null);
  useAltHotkeys(
    {
      S: canSave ? handleSave : null,
      C: saving ? null : onClose,
    },
    rootRef,
    { layer: true },
  );

  const { panelRef, panelStyle, dragHandleProps } = useModalDrag(true);

  const title = `Driver Note - ${unitnumber} ${driverName}`;

  return createPortal(
    <div
      ref={rootRef}
      role="dialog"
      aria-modal="true"
      aria-label={title}
      className="fixed inset-0 z-[200] flex items-center justify-center"
    >
      <div className="absolute inset-0 bg-scrim/50" onClick={saving ? undefined : onClose} />
      <div
        ref={panelRef}
        style={panelStyle}
        className="relative bg-panel rounded-lg shadow-xl max-w-md w-full mx-4"
      >
        <ModalHeader
          title={
            <span className="flex items-center gap-2">
              <ChatBubbleBottomCenterTextIcon className="w-5 h-5 text-blue-300" />
              {title}
            </span>
          }
          onClose={saving ? undefined : onClose}
          dragHandleProps={dragHandleProps}
        />

        <div className="px-6 py-4">
          <label htmlFor="driver-note-textarea" className="block text-sm font-medium text-label mb-1">
            Today's note
          </label>
          <textarea
            id="driver-note-textarea"
            className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-body placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-blue-500 resize-none"
            rows={4}
            placeholder="Enter a note for this driver..."
            value={comment}
            onChange={(e) => setComment(e.target.value)}
            disabled={saving}
            autoFocus
          />
          <p className="mt-1 text-xs text-gray-500 dark:text-gray-400">
            Shown on the lane's comment icon and the Driver Board. Save it empty to clear the note.
          </p>
        </div>

        <div className="flex justify-end gap-3 px-6 py-4 border-t border-gray-200 dark:border-gray-700">
          <button
            type="button"
            onClick={onClose}
            disabled={saving}
            className="px-4 py-2 text-sm font-medium text-label bg-gray-100 dark:bg-gray-700 rounded-lg hover:bg-gray-200 dark:hover:bg-gray-600 disabled:opacity-50"
          >
            Cancel
          </button>
          <button
            type="button"
            onClick={handleSave}
            disabled={!canSave}
            className="px-4 py-2 text-sm font-medium text-white bg-blue-600 rounded-lg hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed"
          >
            {saving ? 'Saving…' : 'Save'}
          </button>
        </div>
      </div>
    </div>,
    document.body,
  );
}

export default DriverCommentModal;
